import type { ITourAnalyticsConfig, ITourStepConfig } from './types';

/**
 * Resolves analytics config by merging user config with defaults.
 *
 * @param config - Optional partial analytics config
 * @param tourId - Tour identifier used as fallback event prefix
 * @returns Fully resolved analytics config with enabled and eventPrefix guaranteed
 */
export function resolveAnalyticsConfig(
  config: ITourAnalyticsConfig | undefined,
  tourId: string,
): Required<Pick<ITourAnalyticsConfig, 'enabled' | 'eventPrefix'>> &
  ITourAnalyticsConfig {
  return {
    enabled: true,
    eventPrefix: `tour_${tourId}`,
    ...config,
  };
}

/**
 * Builds metadata for a tour analytics event.
 *
 * @param tourId - Tour identifier
 * @param extraMetadata - Additional metadata from the tour config
 * @param step - Optional step information
 * @param stepIndex - Optional step index
 * @param totalSteps - Optional total steps
 * @returns Metadata object for the analytics event
 */
export function buildEventMetadata(
  tourId: string,
  extraMetadata?: Record<string, string>,
  step?: ITourStepConfig,
  stepIndex?: number,
  totalSteps?: number,
): Record<string, string> {
  const metadata: Record<string, string> = {
    tourId,
    ...extraMetadata,
  };

  if (step) {
    metadata.stepId = step.id ?? String(stepIndex ?? 0);
    metadata.stepTitle = step.titleKey;
  }

  if (stepIndex !== undefined) {
    metadata.stepIndex = String(stepIndex);
  }

  if (totalSteps !== undefined) {
    metadata.totalSteps = String(totalSteps);
  }

  return metadata;
}

/**
 * Generates the full event name for a tour analytics event.
 *
 * @param eventPrefix - The tour's event prefix
 * @param action - The action suffix (e.g., 'started', 'completed', 'step_viewed')
 * @returns Full event name like `tour_draft_creation_started`
 */
export function getTourEventName(eventPrefix: string, action: string): string {
  return `${eventPrefix}_${action}`;
}
